import React, { useCallback, useEffect, useState } from 'react';
import { BadgeCheck, Plus } from 'lucide-react';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import { albiAPI } from '../services/api';
import '../styles/AlbiSedi.css';

/**
 * AlbiSedi — le liste chiuse dell'iscrizione all'albo.
 *
 * Gli albi sono fissi (migration 190). Le SEDI sono quello che
 * l'amministrazione puo' aggiungere: le 107 province dell'Ordine dei
 * Medici e le regioni degli Psicologi ci sono tutte; Biologi,
 * TSRM-PSTRP e Fisioterapisti hanno sedi territoriali che cambiano per
 * legge, e quando arriva un professionista iscritto in una sede che
 * manca la si aggiunge da qui - con il nome e la forma per la stampa
 * («di Rimini», «dell'Emilia-Romagna»). Chi compila la scheda del
 * medico continua a scegliere da una tendina.
 */
const VUOTA = { codice: '', nome: '', stampa: '' };

const AlbiSedi = () => {
    const [albi, setAlbi] = useState([]);
    const [aperto, setAperto] = useState(null);      // codice dell'albo con il modulo aperto
    const [nuova, setNuova] = useState(VUOTA);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);

    const carica = useCallback(async () => {
        try {
            const r = await albiAPI.elenco();
            setAlbi(r.data?.data?.albi || []);
        } catch (e) { setErrore('Non riesco a leggere gli albi'); }
    }, []);
    useEffect(() => { carica(); }, [carica]);

    const salva = async (codice) => {
        setErrore(null);
        try {
            await albiAPI.aggiungiSede(codice, nuova);
            setAvviso(`Sede «${nuova.nome}» aggiunta.`);
            setNuova(VUOTA);
            setAperto(null);
            carica();
        } catch (e) { setErrore(e?.response?.data?.message || 'Errore nel salvataggio della sede'); }
    };

    const attiva = async (sede, v) => {
        try { await albiAPI.aggiornaSede(sede.id, { attiva: v }); carica(); }
        catch (e) { setErrore(e?.response?.data?.message || 'Errore'); }
    };

    return (
        <div className="as-pagina">
            <IntestazioneSezione
                etichetta="Amministrazione"
                titolo="Albi e sedi"
                icona={<BadgeCheck size={22} aria-hidden="true" />}
                sottotitolo="Le liste da cui si sceglie l'iscrizione all'albo di un professionista. Gli albi sono fissi; le sedi si aggiungono quando servono."
            />
            {errore && <p className="rm-messaggio rm-messaggio-errore">{errore}</p>}
            {avviso && <p className="rm-messaggio rm-messaggio-conferma">{avviso}</p>}

            {albi.map((a) => (
                <section key={a.codice} className="as-albo">
                    <h2>{a.nome} <span className="as-ambito">{{ provincia: 'per provincia', regione: 'per regione', sede: 'per sede territoriale' }[a.ambito]}</span></h2>
                    <ul className="as-sedi">
                        {a.sedi.map((s) => (
                            <li key={s.id} className={s.attiva === false ? 'as-spenta' : ''}>
                                <span className="as-nome">{s.nome}</span>
                                <span className="as-stampa">«Iscritto all'{a.nome} {s.stampa} n. …»</span>
                                <button type="button" className="as-spegni" onClick={() => attiva(s, s.attiva === false)}>
                                    {s.attiva === false ? 'riattiva' : 'nascondi'}
                                </button>
                            </li>
                        ))}
                    </ul>
                    {aperto === a.codice ? (
                        <div className="as-nuova">
                            <label><span>Codice</span>
                                <input value={nuova.codice} maxLength={20} placeholder="Es. FC-RN"
                                       onChange={(e) => setNuova({ ...nuova, codice: e.target.value })} /></label>
                            <label><span>Nome</span>
                                <input value={nuova.nome} maxLength={120} placeholder="Es. Forlì-Cesena e Rimini"
                                       onChange={(e) => setNuova({ ...nuova, nome: e.target.value })} /></label>
                            <label><span>Per la stampa</span>
                                <input value={nuova.stampa} maxLength={160} placeholder="Es. delle province di Forlì-Cesena e Rimini"
                                       onChange={(e) => setNuova({ ...nuova, stampa: e.target.value })} /></label>
                            <button type="button" className="rm-btn rm-btn-primario" onClick={() => salva(a.codice)}>Aggiungi</button>
                            <button type="button" className="rm-btn" onClick={() => { setAperto(null); setNuova(VUOTA); }}>Annulla</button>
                        </div>
                    ) : (
                        <button type="button" className="rm-btn as-aggiungi" onClick={() => setAperto(a.codice)}>
                            <Plus size={14} /> Aggiungi una sede
                        </button>
                    )}
                </section>
            ))}
        </div>
    );
};

export default AlbiSedi;
